import React from "react";
import { Alert, LinearProgress, Stack, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useParams } from "react-router-dom";
import { activityApi } from "../../api/activity";
import { projectsApi } from "../../api/projects";
import ModuleDisabledState from "../../components/dashboard/ModuleDisabledState";
import DesktopWebsiteStats from "../../components/dashboard/stats/DesktopWebsiteStats";
import MobileWebsiteStats from "../../components/dashboard/stats/mobile/MobileWebsiteStats";
import { useProjects } from "../../context/ProjectsContext";
import { isProjectModuleEnabled } from "../../utils/projectModules";
import { toDateInput } from "../../utils/erp";

function startOfMonth() {
  const date = new Date();
  return toDateInput(new Date(date.getFullYear(), date.getMonth(), 1));
}

function rangeForPreset(preset) {
  const now = new Date();
  const today = toDateInput(now);
  if (preset === "today") return { preset, dateFrom: today, dateTo: today };
  if (preset === "7days") {
    const from = new Date(now);
    from.setDate(now.getDate() - 6);
    return { preset, dateFrom: toDateInput(from), dateTo: today };
  }
  if (preset === "30days") {
    const from = new Date(now);
    from.setDate(now.getDate() - 29);
    return { preset, dateFrom: toDateInput(from), dateTo: today };
  }
  return { preset: "month", dateFrom: startOfMonth(), dateTo: today };
}

export default function ProjectStatsPage() {
  const { projectId } = useParams();
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down("md"));
  const { projects, activeProject, setActiveProject, loadProjects } = useProjects();
  const [project, setProject] = React.useState(null);
  const [analytics, setAnalytics] = React.useState(null);
  const [range, setRange] = React.useState(() => rangeForPreset("30days"));
  const [loadingProject, setLoadingProject] = React.useState(true);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");

  const contextProject = React.useMemo(
    () => projects.find((item) => item.slug === projectId || item.id === projectId || item._id === projectId) || activeProject,
    [projects, projectId, activeProject],
  );
  const resolvedProject = project || contextProject;
  const enabled = isProjectModuleEnabled(resolvedProject, "stats");

  React.useEffect(() => {
    if (!projects.length) loadProjects();
  }, [projects.length, loadProjects]);

  React.useEffect(() => {
    let mounted = true;
    setLoadingProject(true);
    projectsApi.get(projectId)
      .then((data) => {
        if (!mounted) return;
        const nextProject = data.project || null;
        setProject(nextProject);
        if (nextProject) setActiveProject(nextProject);
      })
      .catch((requestError) => {
        if (mounted) setError(requestError.message || "No se pudo cargar el proyecto");
      })
      .finally(() => {
        if (mounted) setLoadingProject(false);
      });
    return () => { mounted = false; };
  }, [projectId, setActiveProject]);

  const loadAnalytics = React.useCallback(async () => {
    if (!enabled || !range.dateFrom || !range.dateTo) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError("");
      const response = await activityApi.businessAnalytics(projectId, {
        dateFrom: range.dateFrom,
        dateTo: range.dateTo,
      });
      setAnalytics(response.analytics || null);
    } catch (requestError) {
      setError(requestError.message || "No se pudieron cargar las estadísticas de actividad del negocio");
    } finally {
      setLoading(false);
    }
  }, [enabled, projectId, range.dateFrom, range.dateTo]);

  React.useEffect(() => {
    if (!loadingProject) loadAnalytics();
  }, [loadingProject, loadAnalytics]);

  const selectPreset = (preset) => {
    if (preset === "custom") {
      setRange((current) => ({ ...current, preset: "custom" }));
      return;
    }
    setRange(rangeForPreset(preset));
  };

  const updateRange = (key, value) => {
    setRange((current) => ({ ...current, preset: "custom", [key]: value }));
  };

  if (loadingProject) return <Alert severity="info">Cargando estadísticas del negocio...</Alert>;
  if (!enabled) {
    return (
      <ModuleDisabledState
        projectId={projectId}
        title="Estadísticas"
        description="Este módulo resume la actividad general del negocio a partir del historial: operaciones, equipo, web pública, áreas con movimiento y recorridos."
      />
    );
  }

  const viewProps = {
    analytics,
    range,
    onPreset: selectPreset,
    onRangeChange: updateRange,
    onRefresh: loadAnalytics,
    loading,
    error,
  };

  return (
    <Stack spacing={1.2}>
      {loading ? <LinearProgress sx={{ borderRadius: 999 }} /> : null}
      {mobile ? <MobileWebsiteStats {...viewProps} /> : <DesktopWebsiteStats {...viewProps} />}
    </Stack>
  );
}
